const BASE_URL = "https://medidores-app-production.up.railway.app/api";

function getToken() {
  return localStorage.getItem("token");
}
function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

let _refreshPromise = null;

export async function refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token");
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Sesión expirada");
    localStorage.setItem("token", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data.token;
  })().finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

async function request(path, options = {}, _retry = false) {
  const headers = { "Content-Type": "application/json", ...options.headers };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401 && !_retry && !path.startsWith("/auth/")) {
    try {
      await refreshAccessToken();
      return request(path, options, true);
    } catch {
      logout();
      window.location.href = "/login";
      throw new Error("Sesión expirada. Iniciá sesión nuevamente.");
    }
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error en la solicitud");
  return data;
}

// AUTH
export async function login(email, password) {
  const data = await request("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  const allowedRoles = ["admin", "admin-plus", "administrador-whatsapp"];
  if (!allowedRoles.includes(data.user.role))
    throw new Error("No tenés permisos de administrador");
  localStorage.setItem("token", data.token);
  localStorage.setItem("refreshToken", data.refreshToken ?? "");
  localStorage.setItem("user", JSON.stringify(data.user));
  return data;
}

export function logout() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
}

export function getStoredUser() {
  const u = localStorage.getItem("user");
  return u ? JSON.parse(u) : null;
}

// ── Premios ───────────────────────────────────────────────────────────────────

export async function getPremiosAdmin() {
  return request("/admin/premios");
}

export async function createPremio(data) {
  return request("/admin/premios", { method: "POST", body: JSON.stringify(data) });
}

export async function updatePremio(id, data) {
  return request(`/admin/premios/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deletePremio(id) {
  return request(`/admin/premios/${id}`, { method: "DELETE" });
}

// ── Medidores manuales ────────────────────────────────────────────────────────

export async function getManualMeters({ search, page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (search) params.append("search", search);
  return request(`/admin/meters?${params}`);
}

export async function createManualMeter(data) {
  return request("/admin/meters", { method: "POST", body: JSON.stringify(data) });
}

export async function createManualMetersBatch(data) {
  return request("/admin/meters/batch", { method: "POST", body: JSON.stringify(data) });
}

export async function updateManualMeter(id, data) {
  return request(`/admin/meters/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteManualMeter(id) {
  return request(`/admin/meters/${id}`, { method: "DELETE" });
}

// ── WhatsApp ──────────────────────────────────────────────────────────────────

export async function searchWhatsappMedidor(medidor) {
  return request(`/whatsapp/search?medidor=${encodeURIComponent(medidor)}`);
}

export async function getTodayWhatsappReadings() {
  return request("/whatsapp/today");
}

export async function getWhatsappAdminReadings({ from, to, medidor, page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  if (medidor) params.append("medidor", medidor);
  return request(`/whatsapp/admin/readings?${params}`);
}

export async function deleteWhatsappReading(id) {
  return request(`/whatsapp/${id}`, { method: "DELETE" });
}

export async function createWhatsappReading(formData) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}/whatsapp`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error al guardar");
  return data;
}

export async function getPadronStats() {
  return request("/whatsapp/padron/stats");
}

export async function searchPadronMedidor(medidor) {
  return request(`/whatsapp/padron/search?medidor=${encodeURIComponent(medidor)}`);
}

export async function upsertPadronRow(row) {
  return request("/whatsapp/padron/upsert", {
    method: "POST",
    body: JSON.stringify(row),
  });
}

export async function importPadronCsv(formData) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}/whatsapp/padron/import`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error al importar el padrón");
  return data;
}

// Colaboradores
export async function getEmployees() {
  return request("/admin/employees");
}

export async function createEmployee(data) {
  return request("/admin/employees", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateEmployee(id, data) {
  return request(`/admin/employees/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function uploadEmployeeCredential(id, file) {
  const token = getToken();
  const formData = new FormData();
  formData.append("credential", file);
  const res = await fetch(`${BASE_URL}/admin/employees/${id}/credential`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error al subir credencial");
  return data;
}

export async function uploadEmployeeDocumento(id, campo, file) {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${BASE_URL}/admin/employees/${id}/documento/${campo}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error al subir documento");
  return data;
}

export async function uploadChatFile(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${BASE_URL}/chat/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error al subir archivo");
  return data; // { url, file_type }
}

export async function toggleEmployee(id, active) {
  return request(`/admin/employees/${id}/toggle`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

export async function deleteEmployee(id) {
  return request(`/admin/employees/${id}`, { method: "DELETE" });
}

export async function setUserPassword(id, password) {
  return request(`/admin/employees/${id}/password`, {
    method: "PATCH",
    body: JSON.stringify({ password }),
  });
}

// RUTAS
// data: { name, employee_id, polygon, valor_personalizado }
export async function createRoute(data) {
  return request("/admin/routes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getRoutes() {
  return request("/admin/routes");
}

export async function deleteRoute(id) {
  return request(`/admin/routes/${id}`, { method: "DELETE" });
}

export async function getCompletedAssignments({ page = 1, limit = 20 } = {}) {
  return request(`/admin/routes/completed-assignments?page=${page}&limit=${limit}`);
}

export async function deleteCompletedAssignment(id) {
  return request(`/admin/routes/completed-assignments/${id}`, { method: "DELETE" });
}

export async function marcarPagado(id) {
  return request(`/admin/routes/completed-assignments/${id}/marcar-pagado`, { method: "PATCH" });
}

export async function getRoutesInRevision() {
  return request("/admin/routes/revision");
}

export async function approveRouteRevision(routeId, notas) {
  return request(`/admin/routes/${routeId}/approve-revision`, {
    method: "PATCH",
    body: JSON.stringify({ notas: notas || null }),
  });
}

export async function rejectRouteRevision(routeId, notas) {
  return request(`/admin/routes/${routeId}/reject-revision`, {
    method: "PATCH",
    body: JSON.stringify({ notas: notas || null }),
  });
}

export async function getAssignmentReadings(id) {
  return request(`/admin/routes/assignments/${id}/readings`);
}

export async function repeatRoute(assignmentId) {
  return request(`/admin/routes/assignments/${assignmentId}/repeat`, {
    method: "POST",
  });
}

export async function returnPendingMeters(assignmentId) {
  return request(`/admin/routes/assignments/${assignmentId}/return-pending`, {
    method: "POST",
  });
}

export async function updateAssignmentValor(assignmentId, valor) {
  return request(`/admin/routes/assignments/${assignmentId}/valor`, {
    method: "PATCH",
    body: JSON.stringify({ valor }),
  });
}

export async function getRouteHistory(id) {
  return request(`/admin/routes/${id}/history`);
}

export async function completeRouteAssignment(id, notas) {
  return request(`/admin/routes/${id}/complete-assignment`, {
    method: "PATCH",
    body: JSON.stringify({ notas: notas || null }),
  });
}

// data: { name, employee_id, valor_personalizado }
export async function updateRoute(id, data) {
  return request(`/admin/routes/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// SOLICITUDES DE REGISTRO
export async function getRegistrations(status = "pending") {
  return request(`/registrations?status=${status}`);
}

export async function getRegistration(id) {
  return request(`/registrations/${id}`);
}

export async function approveRegistration(id, zone, role = "lector") {
  return request(`/registrations/${id}/approve`, {
    method: "PATCH",
    body: JSON.stringify({ zone, role }),
  });
}

export async function rejectRegistration(id, reason) {
  return request(`/registrations/${id}/reject`, {
    method: "PATCH",
    body: JSON.stringify({ reason }),
  });
}

export async function deleteRegistration(id) {
  return request(`/registrations/${id}`, { method: "DELETE" });
}

// TARIFAS
export async function getTarifas() {
  return request("/admin/tarifas");
}

export async function updateTarifas(data) {
  return request("/admin/tarifas", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// SETTINGS
export async function getSettings() {
  return request("/admin/settings");
}

export async function updateSetting(key, value) {
  return request(`/admin/settings/${key}`, {
    method: "PATCH",
    body: JSON.stringify({ value }),
  });
}

// MEDICIONES
export async function updateReading(id, data) {
  return request(`/admin/readings/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteReading(id) {
  return request(`/admin/readings/${id}`, { method: "DELETE" });
}

export async function updateMeterInRoute(routeId, meterId, data) {
  return request(`/admin/routes/${routeId}/meters/${meterId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}
export async function getMapClusters({ zoom, minLat, maxLat, minLng, maxLng, userId, from, to, zone, meterCode } = {}) {
  const params = new URLSearchParams({ zoom: zoom ?? 12, minLat, maxLat, minLng, maxLng });
  if (userId)    params.append("user_id",    userId);
  if (from)      params.append("from",       from);
  if (to)        params.append("to",         to);
  if (zone)      params.append("zone",       zone);
  if (meterCode) params.append("meter_code", meterCode);
  return request(`/admin/map/clusters?${params}`);
}

export async function getReadingsForMap({
  userId, from, to, zone, meterCode, segmentacion, limit, offset,
  minLat, maxLat, minLng, maxLng,
} = {}) {
  const params = new URLSearchParams();
  if (userId)       params.append("user_id",       userId);
  if (from)         params.append("from",          from);
  if (to)           params.append("to",            to);
  if (zone)         params.append("zone",          zone);
  if (meterCode)    params.append("meter_code",    meterCode);
  if (segmentacion) params.append("segmentacion",  segmentacion);
  if (limit)     params.append("limit",      limit);
  if (offset)    params.append("offset",     offset);
  if (minLat != null) params.append("min_lat", minLat);
  if (maxLat != null) params.append("max_lat", maxLat);
  if (minLng != null) params.append("min_lng", minLng);
  if (maxLng != null) params.append("max_lng", maxLng);
  const qs = params.toString();
  return request(`/admin/readings/map${qs ? "?" + qs : ""}`);
}

export async function toggleMeterExcluded(routeId, meterId) {
  return request(`/map/routes/${routeId}/meter/${meterId}/exclude`, { method: "PATCH" });
}

export async function getAdminMetersList({ page = 1, limit = 100, search, routeId, status, userId, from, to } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (search)  params.append("search",   search);
  if (routeId) params.append("route_id", routeId);
  if (status)  params.append("status",   status);
  if (userId)  params.append("user_id",  userId);
  if (from)    params.append("from",     from);
  if (to)      params.append("to",       to);
  return request(`/admin/meters-list?${params}`);
}

// NOTIFICACIONES
export async function getNotificationHistory() {
  return request("/admin/notifications/history");
}
export async function getNotificationTemplates() {
  return request("/admin/notifications/templates");
}
export async function createNotificationTemplate(data) {
  return request("/admin/notifications/templates", { method: "POST", body: JSON.stringify(data) });
}
export async function deleteNotificationTemplate(id) {
  return request(`/admin/notifications/templates/${id}`, { method: "DELETE" });
}

// INCIDENCIAS
export async function getAdminIncidents({ page = 1, limit = 50, userId, from, to, code, meterCode } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (userId)    params.append("user_id",    userId);
  if (from)      params.append("from",       from);
  if (to)        params.append("to",         to);
  if (code)      params.append("code",       code);
  if (meterCode) params.append("meter_code", meterCode);
  return request(`/admin/incidents?${params}`);
}

export async function analyzeImage(imageUrl) {
  return request("/admin/analyze-image", {
    method: "POST",
    body: JSON.stringify({ image_url: imageUrl }),
  });
}

export async function deleteAdminIncident(id) {
  return request(`/admin/incidents/${id}`, { method: "DELETE" });
}

export async function getAdminIncidentsCount({ since } = {}) {
  const params = since ? `?since=${encodeURIComponent(since)}` : "";
  return request(`/admin/incidents/count${params}`);
}

export async function analyzeMeterReading(imageUrl, meterId) {
  return request("/admin/analyze-meter-reading", {
    method: "POST",
    body: JSON.stringify({ image_url: imageUrl, meter_id: meterId }),
  });
}

// REVISIÓN RÁPIDA
export async function saveMeterReview(data) {
  return request("/admin/meter-reviews", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getMeterReviews({ routeId, page = 1, limit = 100 } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (routeId) params.append("route_id", routeId);
  return request(`/admin/meter-reviews?${params}`);
}

export async function getReadings({
  userId,
  from,
  to,
  zone,
  meterCode,
  page = 1,
  limit = 50,
} = {}) {
  const params = new URLSearchParams({ page, limit });
  if (userId) params.append("user_id", userId);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  if (zone) params.append("zone", zone);
  if (meterCode) params.append("meter_code", meterCode);
  return request(`/admin/readings?${params}`);
}

// ── FACTURACIÓN / BILLING ─────────────────────────────────────────────────────

export async function getBillingCycles({ user_id, status } = {}) {
  const params = new URLSearchParams();
  if (user_id) params.append("user_id", user_id);
  if (status)  params.append("status",  status);
  const qs = params.toString();
  return request(`/billing/admin/cycles${qs ? "?" + qs : ""}`);
}

export async function getBillingCycleDetail(id) {
  return request(`/billing/admin/cycles/${id}`);
}

export async function createBillingCycle(data) {
  return request("/billing/admin/cycles", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function createGlobalBillingCycles(data) {
  return request("/billing/admin/cycles/global", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateBillingCycle(id, data) {
  return request(`/billing/admin/cycles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function payBillingCycle(id) {
  return request(`/billing/admin/cycles/${id}/pay`, { method: "PATCH" });
}

export async function deleteBillingCycle(id) {
  return request(`/billing/admin/cycles/${id}`, { method: "DELETE" });
}

export async function getCompletedRoutesForBilling(user_id) {
  return request(`/billing/admin/completed-routes?user_id=${user_id}`);
}
